import { type Currency } from '@principle-theorem/accounting';
import {
  TypeGuard,
  isDocRef,
  isINamedDocument,
  isObject,
  isTimestamp,
  type DocumentReference,
  type INamedDocument,
  type Timestamp,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import { type IStaffer } from '../staffer/staffer';
import { DepositCaptureStrategy } from './deposit-capture-strategy';
import { type IDepositPaymentAttempt } from './deposit-payment-attempt';
import { DepositPaymentMode } from './deposit-payment-mode';
import {
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON,
  DEPOSIT_STAFF_ATTENTION_REASON,
  type DepositCaptureStrategyFallbackReason,
  type IDepositStaffAttention,
} from './deposit-request';
import { DepositRequirementSource } from './deposit-requirement-source';
import {
  isDepositTreatmentConfigurationContributionMap,
  type IDepositTreatmentConfigurationContribution,
} from './deposit-settings';

export enum DepositStatus {
  Required = 'required',
  Pending = 'pending',
  Authorised = 'authorised',
  Paid = 'paid',
  Forfeited = 'forfeited',
  Refunded = 'refunded',
  Waived = 'waived',
  Cancelled = 'cancelled',
  Released = 'released',
}

/**
 * Statuses where the practice is still waiting on the patient's money.
 *
 * `Authorised` counts as settled here: the funds are held and will be captured
 * by the capture strategy, so chasing the patient again would be wrong.
 */
export const UNPAID_DEPOSIT_STATUSES: readonly DepositStatus[] = [
  DepositStatus.Required,
  DepositStatus.Pending,
];

/**
 * Whether the practice is still owed this deposit. Gates the "you still owe us"
 * affordances, so a missing deposit reads the same as a settled one.
 */
export function isUnpaidDepositStatus(status?: DepositStatus): boolean {
  return status !== undefined && UNPAID_DEPOSIT_STATUSES.includes(status);
}

export interface IDepositMetadata {
  status: DepositStatus;
  amountCents: number;
  currency: Currency;
  paymentMode: DepositPaymentMode;
  captureStrategy?: DepositCaptureStrategy;
  fallbackReason?: DepositCaptureStrategyFallbackReason;
  treatmentConfigurationDeposits?: Record<
    string,
    IDepositTreatmentConfigurationContribution
  >;
  paymentAttemptRef?: DocumentReference<IDepositPaymentAttempt>;
  captureBefore?: Timestamp;
  description?: string;
  paidAt?: Timestamp;
  waivedBy?: INamedDocument<IStaffer>;
  waiverReason?: string;
  forfeitedAt?: Timestamp;
  refundedAt?: Timestamp;
  releasedAt?: Timestamp;
  attention?: IDepositStaffAttention;
  /**
   * Why the deposit was required when the appointment was booked. Lets the
   * chip and sidebar explain a deposit whose settings have since changed.
   * Absent on deposits recorded before this was kept.
   */
  source?: DepositRequirementSource;
  /** Staff changed the amount from the configured default when booking. */
  amountOverridden?: boolean;
  supersedeClaim?: {
    newRequestId: string;
    claimedAt: Timestamp;
  };
}

export const isDepositMetadata = TypeGuard.interface<IDepositMetadata>({
  status: TypeGuard.enumValue(DepositStatus),
  amountCents: isNumber,
  currency: isString,
  paymentMode: TypeGuard.enumValue(DepositPaymentMode),
  captureStrategy: TypeGuard.nilOr(TypeGuard.enumValue(DepositCaptureStrategy)),
  fallbackReason: TypeGuard.nilOr(
    TypeGuard.enumValue(DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON)
  ),
  treatmentConfigurationDeposits: TypeGuard.nilOr(
    isDepositTreatmentConfigurationContributionMap
  ),
  paymentAttemptRef: TypeGuard.nilOr(isDocRef),
  captureBefore: TypeGuard.nilOr(isTimestamp),
  description: TypeGuard.nilOr(isString),
  paidAt: TypeGuard.nilOr(isObject),
  waivedBy: TypeGuard.nilOr(isINamedDocument),
  waiverReason: TypeGuard.nilOr(isString),
  forfeitedAt: TypeGuard.nilOr(isObject),
  refundedAt: TypeGuard.nilOr(isObject),
  releasedAt: TypeGuard.nilOr(isObject),
  attention: TypeGuard.nilOr(
    TypeGuard.interface<IDepositStaffAttention>({
      reason: TypeGuard.enumValue(DEPOSIT_STAFF_ATTENTION_REASON),
      flaggedAt: isTimestamp,
    })
  ),
  source: TypeGuard.nilOr(TypeGuard.enumValue(DepositRequirementSource)),
  amountOverridden: TypeGuard.nilOr(isBoolean),
  supersedeClaim: TypeGuard.nilOr(
    TypeGuard.interface<NonNullable<IDepositMetadata['supersedeClaim']>>({
      newRequestId: isString,
      claimedAt: isTimestamp,
    })
  ),
});
